import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ExecuteRequestsQuery,
  ExecuteRequestsQueryVariables,
} from 'insights/analytics-queries/overflow-menu/__generated/ExecuteRequestsQuery';
import notifications from 'notifications';
import canRequestsBeExecuted from 'requests/canRequestsBeExecuted';
import ExecuteRequestFlow from 'requests/execution/ExecuteRequestFlow';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {RequestGraphQL} from '../../../../graphql-fragments/requests/__generated/RequestGraphQL';
import buildTakeActionOnColumnOption, {ColumnWithIds} from './buildTakeActionOnColumnOption';

type Request = ExtractByTypename<ExecuteRequestsQuery['requests'][number], 'RequestNode'>;

export const EXECUTE_REQUESTS_QUERY: DocumentNode = gql`
  # requests.sampleGroups does not query id
  # eslint-disable benchlint/id-field-required
  query ExecuteRequestsQuery($requestIds: [StrictString!]!) @skipAudit @engComponent(name: INSIGHTS) {
    requests(ids: $requestIds) {
      ... on RequestNode {
        ...RequestGraphQL
        schema {
          ...RequestSchemaGraphQL
        }
        sampleGroups {
          samples {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Request').fragment}
  ${getFragmentWrapper('RequestSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface RequestColumn {
  name: string;
  uniqueRequestIds: Array<string>;
}

async function handleSelectColumn({uniqueIds}: ColumnWithIds): Promise<void> {
  assertAndContinue(uniqueIds.length === new Set(uniqueIds).size, 'Column items have duplicates', {
    uniqueIds,
  });
  const INFO_TOAST_ID = 'checking-requests-executable';
  notifications.info('Checking whether requests are executable...', {id: INFO_TOAST_ID, duration: 0});

  const result = await doQuerySafe<ExecuteRequestsQuery, ExecuteRequestsQueryVariables>(
    {query: EXECUTE_REQUESTS_QUERY},
    {variables: {requestIds: uniqueIds}}
  );
  if (result.requests) {
    const inaccessibleRequests = result.requests.filter((request) => isInaccessible(request));
    if (inaccessibleRequests.length > 0) {
      notifications.error(
        `You do not have permission to execute ${benchlingUtil.pluralize(
          inaccessibleRequests.length,
          'request'
        )} ${inaccessibleRequests
          .map((request) => (request as InaccessibleInfoGraphQL).invalidId)
          .join(', ')}`
      );
      return;
    }
    const requestSchemaByRequestId = objectFromEntries(
      result.requests.map((request: Request) => [request.id, graphQLToREST('RequestSchema', request.schema)])
    );
    const requestIdToRequestHasSamples = objectFromEntries(
      result.requests.map((request: Request) => [
        request.id,
        request.sampleGroups.filter((sampleGroup) => sampleGroup.samples.length !== 0).length > 0,
      ])
    );
    const requests = graphQLArrayToREST('Request', result.requests as Array<RequestGraphQL>);
    const {explanationMessage} = canRequestsBeExecuted({
      requests,
      requestSchemaByRequestId,
      requestIdToRequestHasSamples,
    });

    notifications.close(INFO_TOAST_ID);
    if (explanationMessage == null) {
      LegacyModalContainer.open(
        ExecuteRequestFlow,
        <ExecuteRequestFlow
          requests={requests}
          initialFilters={[]}
          requestSchemasByRequestId={requestSchemaByRequestId}
          onRequestClose={() => {
            LegacyModalContainer.close(ExecuteRequestFlow);
          }}
        />
      );
    } else {
      notifications.error(explanationMessage);
    }
  }
}

export default buildTakeActionOnColumnOption({
  getOptionTitle: () => `Execute ${getLegacyRequestObjectName({lowerCase: true})}`,
  getNoColumnsMessage: () =>
    `No columns for ${getLegacyRequestObjectName({plural: true, lowerCase: true})} found`,
  icon: 'request',
  filterCellContent: (cellContent) => APIUtil.isRequest(cellContent),
  handleSelectColumn,
});
